import {
  ChangeDetectionStrategy,
  Component,
  ComponentFactoryResolver,
  Injector,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {PlayerBase} from '../../player/player.base';
import {IPlugin} from '@signage/interface/dist/IPlugin';
import {IScene} from '../../interfaces/IScene';
import {IPlayer} from "../../interfaces/IPlayer";
import {Log, LogTag} from "../../services/Log";
import {concatMap, forkJoin, mergeAll, mergeWith, Observable, of, switchMap, takeUntil} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {CachingService} from "../../services/caching/caching.service";



@Component({
  selector: 'app-scene',
  template: '<div #component (click)="onSelect()"><template #playercontainer></template></div>',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SceneComponent extends PlayerBase implements IScene {
  resolver: ComponentFactoryResolver;
  script;
  childMap = {};

  @ViewChild('playercontainer', {read: ViewContainerRef, static: true})
  entry: ViewContainerRef;

  childProviderData: any;

  static globalPlayerId = 10000000; // workaround to support catalog that saved in legacy studio

  constructor(protected injector: Injector) {
    super(injector);
    this.resolver = injector.get(ComponentFactoryResolver);
  }

  display(show) {

  }


  update(playerObject: any, action: string, itemIds: Array<string>): Observable<any> {
    const logInfo = Log.getLogInfo("SceneComponent", "update", []);
    return of('update').pipe(
      switchMap(r1 => {
        this.playerObject = playerObject;
        this.data = playerObject.Data[0];
        let childPlayer: PlayerBase;
        if (action == "rebuild") {
          Log.d(logInfo, LogTag.SCENE_UPDATE, "rebuild");
          this.cleanChildren();   // this line cause a crash in studio
          this.childMap = {};
          this.playerObject = playerObject;
          this.data = playerObject.Data[0];
          this.playerService = this.injector.get('playerService');
          this.component.nativeElement.className = "root-scene";
          return this.load(playerObject).pipe(
            mergeWith(of('update-ready'))
          )
        } else {
          const id = this.playerObject.$.id;
          if (itemIds.includes(id)) { // root scene
            Log.d(logInfo, LogTag.SCENE_UPDATE, "root scene: " + action);
            return this.load(playerObject);
          } else { // child
            if (action == "reorder") {
              Log.d(logInfo, LogTag.SCENE_UPDATE, "reorder");
              let index = 0;
              for (let player of this.data.Scene[0].Players[0].Player) {
                const id = player.$.id;
                childPlayer = this.childMap[id];
                if (childPlayer != null) {
                  this.entry.insert(childPlayer.hostView, index);
                  index++;
                }
              }
              return of('update-ready');
            } else if (action == "delete") {
              Log.d(logInfo, LogTag.SCENE_UPDATE, "delete items");
              itemIds.forEach(id => {
                childPlayer = this.childMap[id];
                if (childPlayer != null) {
                  this.removeComponent(childPlayer);
                  childPlayer.destroy();
                  delete this.childMap[id];
                }
              })
              return of('update-ready');
            } else if (action == "update") {
              Log.d(logInfo, LogTag.SCENE_UPDATE, "update items");
              if (this.data.Scene[0].Players[0] != "") {
                const obsList = [];
                for (let player of this.data.Scene[0].Players[0].Player) {
                  const id = player.$.id;
                  if (itemIds.includes(id)) {
                    childPlayer = this.childMap[id];
                    if (childPlayer != null) {
                      this.updateMovable(childPlayer, player); //???
                      if (player.$.src) {
                        const player2 = JSON.parse(JSON.stringify(childPlayer.playerObject));
                        player2.Data[0].Layout = player.Data[0].Layout;
                        obsList.push(childPlayer.load(player2));
                      } else {
                        obsList.push(childPlayer.load(player));
                      }
                    }
                  }
                }
                return forkJoin(obsList).pipe(
                  switchMap(r => of('update-ready'))
                )
              }
            } else if (action == "add") {
              Log.d(logInfo, LogTag.SCENE_UPDATE, "add")
              const obsList = [];
              for (let player of this.data.Scene[0].Players[0].Player) {
                const id = player.$.id;
                if (itemIds.includes(id)) {
                  obsList.push(this.addPlayer(player));
                }
              }
              return forkJoin(obsList).pipe(
                mergeWith(of('update-ready')),
                tap({
                  complete: () => {
                    Log.d(logInfo, LogTag.SCENE_UPDATE, "reorder")
                    let index = 0;
                    for (let player of this.data.Scene[0].Players[0].Player) {
                      const id = player.$.id;
                      childPlayer = this.childMap[id];
                      if (childPlayer != null) {
                        this.entry.insert(childPlayer.hostView, index);
                        index++;
                      }
                    }
                  }
                })
              )
            }
          }
        }
      })
    )
  }

  cleanChildren() {
    for (const key in this.childMap) {
      const childPlayer = this.childMap[key];
      if (childPlayer.component != null) {
        childPlayer.component.nativeElement.classList.remove("moveable");
      }
      childPlayer.destroy();
    }
    this.childMap = null;

    while (this.entry.length > 0) {
      this.entry.remove(0);
    }
  }

  addPlayer(p): Observable<any> {
    const logInfo = Log.getLogInfo("SceneComponent", "addPlayer", []);
    return of(p).pipe(
      concatMap(player => {
        let childPlayer: PlayerBase;
        if (player.$.src) {
          const sceneId = player.$.src;
          return (<CachingService>this.playerService.caching).getRecord("scenes", sceneId).pipe(
            switchMap(scene => {
              if (scene != null && scene.changelist_id != null) {
                childPlayer = this.createComponent(SceneComponent, null, this.editMode, -1);
                this.updateMovable(childPlayer, player);
                (<SceneComponent>childPlayer).originalSize = scene.json.Player.Data[0].Layout[0].$;
                const player2 = JSON.parse(JSON.stringify(scene.json.Player));
                player2.Data[0].Layout = player.Data[0].Layout;
                this.childMap[player.$.id] = childPlayer;
                return childPlayer.load(player2);
              } else {
                // Scene was deleted
                return of('deleted');
              }
            })
          )
        } else {
          const playerId = player.$.player;
          const componentClass: any = this.playerService.getComponentClasses(playerId);
          const blockingTime = this.playerService.getComponentBlockingTime(playerId);
          if (componentClass == null) {
            Log.e(logInfo, LogTag.PLAYER, "Error: playerId=" + playerId);
          } else {
            childPlayer = this.createComponent(componentClass, player, this.editMode, blockingTime);
            this.updateMovable(childPlayer, player);
            this.childMap[player.$.id] = childPlayer;
            return childPlayer.load(player);
          }
        }
      })
    )
  }

  checkCircular() {
    const sceneId = this.playerObject.$.id;
    if (sceneId == null) {
      return false; //???
    }
    let player = this.parentPlayer;
    while (player != null) {
      if (sceneId == player.playerObject.$.id) {
        this.playerService.circularSceneId = sceneId;
        return true;
      }
      player = player.parentPlayer;
    }
    return false;
  }

  loadScript(): Observable<any> {
    if (this.parentPlayer == null && this.playerObject.$.scheduleId != null) { //??? temp
      const className = "HomeHeater";
      const params = '{"eventName":"knx"}'; // '{"eventName":"knx"}' // item.$.params;
      return this.playerService.loadScript(1).pipe(
        switchMap(r => {
          this.script = <IPlugin>this.playerService.createScriptClass(className, params);
          const data = JSON.parse(params);
          return this.script.load(<any>this.injector, data, this.actualWidth, this.actualHeight).pipe(
            switchMap(r => {
              return of("a");
            })
          )
        })
      )
    }
    return of('???');
  }

  load(playerObject: any): Observable<any> {
    const logInfo = Log.getLogInfo("SceneComponent", "load", []);
    let self;
    return super.load(playerObject).pipe(
      switchMap(playerBase => {
        self = playerBase;
        if (this.checkCircular()) {
          Log.w(logInfo, LogTag.PLAYER, 'Circular Dependencies in sceneId:' + this.playerService.circularSceneId);
          this.showMsg("Circular ERROR", "scene id: " + +this.playerObject.$.id)
          return of(null);
        } else {
          return of(self);
        }
      }),
      switchMap(res => {
        if (res == null) {
          return of(null);
        }

        const obsList = [];
        if (this.originalSize != null) {
          this.scaleX = (this.actualWidth / this.originalSize.width);
          this.scaleY = (this.actualHeight / this.originalSize.height);
        }
        let childPlayer: PlayerBase;
        if (this.data.Scene[0].Players[0] != "") {
          for (let player of this.data.Scene[0].Players[0].Player) {
            const id = player.$.id;
            childPlayer = this.childMap[id];
            if (childPlayer == null) {
              if (player.$.src) {
                const childPlayer = this.createComponent(SceneComponent, null, this.editMode, -1);
                this.childMap[player.$.id] = childPlayer;
                obsList.push(this.loadNestedScene(childPlayer, player));
              } else {
                const playerId = player.$.player;
                const componentClass: any = this.playerService.getComponentClasses(playerId);
                const blockingTime = this.playerService.getComponentBlockingTime(playerId);
                if (componentClass == null) {
                  Log.e(logInfo, LogTag.MAIN, "Error: playerId=" + playerId);
                  continue;
                }

                childPlayer = this.createComponent(componentClass, player, this.editMode, blockingTime);

                childPlayer.display(false);

                // workaround to support catalog that saved in legacy studio
                if (player.$.id==null) {
                  player.$.id = SceneComponent.globalPlayerId;
                  SceneComponent.globalPlayerId++;
                }

                this.childMap[player.$.id] = childPlayer;
                this.updateMovable(childPlayer, player);

                const load$ = childPlayer.load(player).pipe(
                  switchMap( child => child.play(false)),
                  mergeWith(childPlayer.registerCommands())
                )
                obsList.push(load$);
              }
            } else {  // in case we do update to the scene, we don't rebuild children
              if (player.$.src) {
                const sceneId = player.$.src;
                childPlayer.playerObject.Data[0].Layout = player.Data[0].Layout;
                childPlayer.loadPosAndSize();
              } else {
                obsList.push(childPlayer.load(player));
              }
            }
          }
        }
        const load$ = (obsList.length>0) ? forkJoin(obsList) : of('empty');
        return of(load$, this.registerCommands()).pipe(mergeAll());
        //return load$;
      }),
      catchError(err => this.logError(err)),
      switchMap(a => of(self)),
      takeUntil(this.finish$)
    )
  }

  loadAnimation() {
  }

  unload(): Observable<any> {
    const self = this;
    const list = [];
    for (const key in this.childMap) {
      const childPlayer = this.childMap[key];
      list.push(childPlayer.unload());
    }
    if (list.length>0) {
     return forkJoin(list).pipe(
       map( a=> self)
     )
    } else {
      return of(self);
    }
  }

  createComponent(cmpClass: any, playerObject, editMode: boolean, blockingTime): PlayerBase {
    const logInfo = Log.getLogInfo("SceneComponent", "createComponent", []);
    let childComponent: PlayerBase;
    const useComponent = cmpClass.prototype.constructor.useComponent; // only false in case of RSS
    let gpu = false; // if it's run in studio it will always use html.
    if (this.editMode == false && playerObject != null) {
      //if (playerObject?.$?.gpu != null) {
      //  gpu = playerObject.$.gpu == '1';
      //} else {
      gpu = cmpClass.prototype.constructor.defaultGpu;
      //}
    }
    if (gpu) {
      childComponent = new cmpClass(this.injector, this.resolver);
    } else {
      Log.d(logInfo, LogTag.PIXI, 'create Angular class: ' + cmpClass.name);
      const factory = this.resolver.resolveComponentFactory(cmpClass);
      const componentRef = this.entry.createComponent(factory);
      childComponent = <PlayerBase>componentRef.instance;
      childComponent.hostView = componentRef.hostView;
    }
    childComponent.init(this, editMode, blockingTime);
    return childComponent;
  }

  removeComponent(player: IPlayer) {
    if (this.entry != null) {
      const i = this.entry.indexOf((<PlayerBase>player).hostView)
      if (i != -1) {
        this.entry.remove(i);
        this.changeDetectorRef?.detectChanges();
      }
    }
  }

  updateMovable(childPlayer, player) {
    if (childPlayer != null && childPlayer.component != null && this.component.nativeElement.classList.contains("root-scene")) {
      const invisible = (player.Data[0].Layout[0].$.invisible == "true");
      const lock = (player.Data[0].Layout[0].$.locked == "true");
      if (lock || invisible) {
        childPlayer.component.nativeElement.classList.remove("moveable");
        childPlayer.component.nativeElement.style.pointerEvents = "none";
      } else {
        childPlayer.component.nativeElement.classList.add("moveable");
        childPlayer.component.nativeElement.style.pointerEvents = "";
      }
      childPlayer.component.nativeElement.style.visibility = invisible ? "hidden" : "visible";
      childPlayer.component.nativeElement.id = player.$.id;
    }
  }

  makeRequest() {
    return new Observable(sub => {
      sub.next('1');
      sub.complete();
    })
  }

  loadNestedScene(childPlayer, player): Observable<any> {
    const logInfo = Log.getLogInfo("SceneComponent", "loadNestedScene", []);
    return of(player).pipe(
      switchMap(player => {
        const sceneId = player.$.src;
        return (<CachingService>this.playerService.caching).getRecord("scenes", sceneId).pipe(
          switchMap(scene => {
            if (scene != null && scene.changelist_id != null) {
              this.updateMovable(childPlayer, player);
              (<SceneComponent>childPlayer).originalSize = scene.json.Player.Data[0].Layout[0].$;
              const player2 = JSON.parse(JSON.stringify(scene.json.Player));
              player2.Data[0].Layout = player.Data[0].Layout;
              return childPlayer.load(player2);
            } else {
              return new Observable(sub => {
                Log.w(logInfo, LogTag.PLAYER, 'Scene was deleted')
                childPlayer.component.nativeElement.id = player.$.id;
                childPlayer.component.nativeElement.classList.add("moveable");
                childPlayer.effectActions = [];
                childPlayer.playerObject = player;
                childPlayer.data = childPlayer.playerObject.Data[0];
                childPlayer.loadPosAndSize();
                //childPlayer.component.nativeElement.style.position='absolute';
                //childPlayer.component.nativeElement.style.width='100px';
                //childPlayer.component.nativeElement.style.height='100px';
                childPlayer.showMsg('Scene ERROR', 'Scene ' + player.$.src + ' was deleted');
                sub.next('delete')
                sub.complete();
              })
            }

          })
        )
      })
    )
  }

  getObsList() {
    const obsList = [];
    for (const key in this.childMap) {
      const p: PlayerBase = this.childMap[key];
      obsList.push(p.registerCommands()); //???
    }
    return obsList;
  }

  stop(): void {
    super.stop();
    for (const key in this.childMap) {
      const p = this.childMap[key];
      p.stop();
    }
  }

  /*???
  invalidateData(providerData: any): Observable<any> {
    this.providerData = providerData;
    const obsList = [];
    for (const key in this.childMap) {
      const p = this.childMap[key];
      obsList.push(p.invalidateData(providerData));
    }
    return forkJoin(obsList);
  }
   */

  onSelect() {
    //??? event.stopPropagation();
    //??? this.playerService.eventParams.dispatch('sceneClicked', this, false);
  }

  addContent(statTag, statField, duration) {
    if (this.childProviderData==null) {
      this.playerService.stats.addContent(statTag, 1, this.playerObject.$.id, duration);
    } else {
      let contentId = String(this.childProviderData[statField]);
      contentId = contentId.substring(0, 16);
      contentId = contentId.replace(/[^a-zA-Z0-9]/g, '_');
      this.playerService.stats.addContent(statTag, 2, contentId, duration);
    }
  }

  destroy(): void {
    for (const key in this.childMap) {
      const childPlayer = this.childMap[key];
      if (childPlayer.component != null) {
        childPlayer.component.nativeElement.classList.remove("moveable");
      }
      childPlayer.destroy();
    }
    this.childMap = null;

    while (this.entry.length > 0) {
      this.entry.remove(0);
    }


    if (this.script != null) {
      this.script.destroy();
    }
    super.destroy();
  }
}
